import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "chunk-reload-ts";

/**
 * Lazy import that recovers from stale chunk references after a new deploy.
 * If a dynamic import fails (old hashed filename no longer on the CDN),
 * retry once, then hard-reload the page (max once per 10s to avoid loops).
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      // Give the network one more chance (transient failure).
      try {
        await new Promise((r) => setTimeout(r, 400));
        return await factory();
      } catch {
        const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
        if (Date.now() - last > 10_000) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          window.location.reload();
          // Never resolves; page is reloading.
          return new Promise<{ default: T }>(() => {});
        }
        throw err;
      }
    }
  });
}
